import { endPoints, GA, PLATFORM } from './../../constants/index'
import { getCookie, setCookie } from './../../helpers/Cookie'
import { fetchApi } from './../../helpers/Api'
import {
	APIMethods,
	ReqBodyWithHeaders,
	ReqBodyWithoutHeaders,
} from './../../types/state/apiTypes'
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { Gender, User, UserLocationType, ViewTypes } from '../../types/state'
import {
	setErrorStatus,
	setLoadingStatus,
	updateLogin,
	updateView,
	updateVotingClose,
} from './meta'
import { store } from '../store'
import { loadTranslations } from './translation'

const initialState: User = {
	email: '',
	name: '',
	gender: Gender.MALE,
	sessionId: '',
	startDateTime: 0,
	endDateTime: 0,
	timelineId: '',
	phone: '',
	id: null,
	hasPlacedOrder: false,
	latitude: null,
	longitude: null,
	userWinning: {},
	winnerList: [],
}

//External
export const validateSession = createAsyncThunk(
	'user/validateSession',
	async (client: string, thunkAPI) => {
		thunkAPI.dispatch(setLoadingStatus(true))
		const sessionId = getCookie('frontend')
		if (sessionId === '') {
			const newSessionObj: ReqBodyWithoutHeaders = {
				method: APIMethods.POST,
				url: endPoints.userSession,
				client: client,
			}
			thunkAPI.dispatch(generateNewSession(newSessionObj))
		} else {
			const validateObj: ReqBodyWithHeaders = {
				method: APIMethods.GET,
				url: endPoints.validate,
				client: client,
				sessionId: sessionId,
			}
			thunkAPI.dispatch(validateCurrentSession(validateObj))
		}
	}
)

export const authenticateUser = createAsyncThunk(
	'user/login',
	async (reqObj: ReqBodyWithHeaders, thunkAPI) => {
		thunkAPI.dispatch(setLoadingStatus(true))
		const response = await fetchApi(reqObj, true)
		if (response.status === 200) {
			const sessionId = response.result.token
			if (reqObj.whatsAppChecked) {
				const whatsAppObj: ReqBodyWithHeaders = {
					method: APIMethods.PUT,
					url: endPoints.whatsApp,
					sessionId: sessionId,
					client: reqObj.client,
				}
				fetchApi(whatsAppObj, true)
			}
			const userObj: ReqBodyWithHeaders = {
				method: APIMethods.GET,
				url: endPoints.userDetails,
				sessionId: sessionId,
				client: reqObj.client,
			}

			window.clevertap.onUserLogin.push({
				Site: {
					Identity: response.result.telephone,
				},
			})

			window.clevertap.event.push('Pages Viewed', {
				'Page Name': GA.verifyOtpPage,
				Phone: response.result.telephone,
			})

			thunkAPI.dispatch(getCurrentUserDetails(userObj))
		} else {
			thunkAPI.dispatch(setLoadingStatus(false))
			thunkAPI.dispatch(
				setErrorStatus({
					status: true,
					message: 'Please enter a valid OTP',
				})
			)
			thunkAPI.dispatch(updateView(ViewTypes.OTP))
		}
	}
)

export const sendOTP = createAsyncThunk(
	'/sendOTP',
	async (reqObj: ReqBodyWithHeaders, thunkAPI) => {
		thunkAPI.dispatch(setLoadingStatus(true))
		const response = await fetchApi(reqObj, true)
		if (response.status === 200) {
			thunkAPI.dispatch(setLoadingStatus(false))
			thunkAPI.dispatch(
				setErrorStatus({
					status: false,
					message: '',
				})
			)
			thunkAPI.dispatch(updateView(ViewTypes.OTP))
		} else {
			thunkAPI.dispatch(setLoadingStatus(false))
			thunkAPI.dispatch(
				setErrorStatus({
					status: true,
					message: 'Error! Failed to send OTP',
				})
			)
			thunkAPI.dispatch(updateView(ViewTypes.LOGIN))
		}
	}
)

export const saveLeads = createAsyncThunk(
	'user/leads',
	async (reqObj: ReqBodyWithHeaders, thunkAPI) => {
		thunkAPI.dispatch(setLoadingStatus(true))
		const response = await fetchApi(reqObj, true)
		if (response?.id) {
			thunkAPI.dispatch(checkVotingClose(reqObj.sessionId))
			// thunkAPI.dispatch(setLoadingStatus(false))
			// thunkAPI.dispatch(setErrorStatus({ status: false, message: '' }))
			// thunkAPI.dispatch(updateView(ViewTypes.VOTES))
		} else {
			thunkAPI.dispatch(setLoadingStatus(false))
			thunkAPI.dispatch(
				setErrorStatus({
					status: true,
					message: 'Failed to save your data. Please try again',
				})
			)
			thunkAPI.dispatch(updateView(ViewTypes.USER_INFO))
		}
	}
)

//Internal
const validateCurrentSession = createAsyncThunk(
	'user/validateCurrentSession',
	async (reqObj: ReqBodyWithHeaders, thunkAPI) => {
		const { sessionId } = reqObj
		const validateResponse = await fetchApi(reqObj, true)
		if (validateResponse.status === 200) {
			const userObj: ReqBodyWithHeaders = {
				method: APIMethods.GET,
				url: endPoints.userDetails,
				sessionId: sessionId,
				client: reqObj.client,
			}
			thunkAPI.dispatch(getCurrentUserDetails(userObj))
		} else {
			const newSessionObj: ReqBodyWithoutHeaders = {
				method: APIMethods.POST,
				url: endPoints.userSession,
				client: 'desktop',
			}
			thunkAPI.dispatch(generateNewSession(newSessionObj))
		}
	}
)

const generateNewSession = createAsyncThunk(
	'user/newSession',
	async (reqObj: ReqBodyWithoutHeaders, thunkAPI) => {
		const response = await fetchApi(reqObj)
		if (response.status === 200) {
			thunkAPI.dispatch(setLoadingStatus(false))
			thunkAPI.dispatch(
				setErrorStatus({
					status: false,
					message: '',
				})
			)
			thunkAPI.dispatch(updateView(ViewTypes.LOGIN))
			return { sessionId: response.result.id }
		} else {
			thunkAPI.dispatch(setLoadingStatus(false))
			thunkAPI.dispatch(
				setErrorStatus({
					status: true,
					message: 'Error! Please try after some time',
				})
			)
			thunkAPI.dispatch(updateView(ViewTypes.LANDING))
		}
	}
)

const checkVotingClose = createAsyncThunk(
	'check/voting',
	async (sessionId: string, thunkAPI) => {
		const obj: ReqBodyWithHeaders = {
			url: endPoints.timeline,
			method: APIMethods.GET,
			client: 'desktop',
			sessionId: sessionId,
		}
		const response = await fetchApi(obj, true)
		if (response.id) {
			thunkAPI.dispatch(setLoadingStatus(false))
			if (store.getState().meta.nextRouteParams === 'winners') {
				thunkAPI.dispatch(updateView(ViewTypes.SCRATCH))
			} else {
				thunkAPI.dispatch(updateView(ViewTypes.VOTES))
			}
			thunkAPI.dispatch(updateVotingClose(false))
			thunkAPI.dispatch(updateTimelineId(response.id))
			return {
				sessionId: sessionId,
				startTime: response.startDateTime,
				endTime: response.endDateTime,
			}
		} else {
			if (store.getState().meta.nextRouteParams === 'winners') {
				thunkAPI.dispatch(updateView(ViewTypes.SCRATCH)) //redirect winner if routeparams is winners
			} else {
				// to directly jump to VOTES page change ViewTypes.OFFERS ->  ViewTypes.VOTES
				thunkAPI.dispatch(updateView(ViewTypes.OFFERS))
			}
			// thunkAPI.dispatch(updateView(ViewTypes.OFFERS))
			thunkAPI.dispatch(updateVotingClose(true))
			return { sessionId: sessionId }
		}
	}
)

const getCurrentUserDetails = createAsyncThunk(
	'user/currentUser',
	async (reqObj: ReqBodyWithHeaders, thunkAPI) => {
		const { sessionId } = reqObj
		const userRes = await fetchApi(reqObj, true)
		if (userRes.status === 200) {
			const voterCheckObj: ReqBodyWithHeaders = {
				method: APIMethods.GET,
				url: `${endPoints.voterCheck}?platformName=${PLATFORM}&phoneNumber=${userRes.result.telephone}&phoneCode=%2B91`,
				sessionId: sessionId,
				client: reqObj.client,
			}
			const userObj = {
				email: userRes.result.email,
				name: userRes.result.firstName,
				gender:
					userRes.result.gender === 'male' ? Gender.MALE : Gender.FEMALE,
				phone: userRes.result.telephone,
				id: userRes.result.id,
				hasPlacedOrder: userRes.result.hasPlacedOrder,
			}
			thunkAPI.dispatch(verifyFirstVoted(voterCheckObj))
			thunkAPI.dispatch(updateLogin(true))
			return { sessionId, userObj }
		} else {
			thunkAPI.dispatch(setLoadingStatus(false))
			thunkAPI.dispatch(updateView(ViewTypes.LOGIN))
			thunkAPI.dispatch(updateLogin(false))
			thunkAPI.dispatch(
				setErrorStatus({
					status: false,
					message: '',
				})
			)
			return { sessionId, userObj: initialState }
		}
	}
)

const verifyFirstVoted = createAsyncThunk(
	'user/firstVote',
	async (reqObj: ReqBodyWithHeaders, thunkAPI) => {
		const checkVoterRes = await fetchApi(reqObj, true)
		if (checkVoterRes.status === 200) {
			thunkAPI.dispatch(setLoadingStatus(false))
			thunkAPI.dispatch(setErrorStatus({ status: false, message: '' }))
			// thunkAPI.dispatch(updateView(ViewTypes.USER_INFO))

			if (checkVoterRes.result) {
				thunkAPI.dispatch(checkVotingClose(reqObj.sessionId))
				thunkAPI.dispatch(
					loadTranslations({
						language: checkVoterRes.result.regionalLanguageProficiency,
					})
				)
			} else {
				thunkAPI.dispatch(updateView(ViewTypes.USER_INFO))
			}
		} else {
			thunkAPI.dispatch(setLoadingStatus(false))
			thunkAPI.dispatch(
				setErrorStatus({
					status: true,
					message: 'Error! Please try after some time',
				})
			)
			// to directly jump to VOTES page change ViewTypes.LANDING ->  ViewTypes.VOTES
			// to directly jump to USER_INFO flow page change ViewTypes.LANDING ->  ViewTypes.USER_INFO
			thunkAPI.dispatch(updateView(ViewTypes.LANDING))
		}
	}
)

export const getWinnersList = createAsyncThunk(
	'khc/winners-list',
	async (reqObj: ReqBodyWithoutHeaders, thunkAPI) => {
		thunkAPI.dispatch(setLoadingStatus(true))
		const response = await fetchApi(reqObj, true)
		if (response && response.length) {
			thunkAPI.dispatch(setLoadingStatus(false))
			thunkAPI.dispatch(
				setErrorStatus({
					status: false,
					message: '',
				})
			)
			const timeLineReqObj: ReqBodyWithHeaders = {
				method: APIMethods.GET,
				url: endPoints.winnerTimelineApi.replace(
					'{timelineId}',
					response[0]?.timelineId
				),
				client: reqObj.client,
				sessionId: reqObj.sessionId,
			}

			thunkAPI.dispatch(getWinnerTimeline(timeLineReqObj))
			return { result: response }
		} else {
			thunkAPI.dispatch(setLoadingStatus(false))
			thunkAPI.dispatch(
				setErrorStatus({
					status: true,
					message: 'Error! Please try after some time',
				})
			)
		}
	}
)

export const getUserWinnings = createAsyncThunk(
	'khc/winnings',
	async (reqObj: ReqBodyWithoutHeaders, thunkAPI) => {
		thunkAPI.dispatch(setLoadingStatus(true))
		const response = await fetchApi(reqObj, true)
		if (response.status === 200) {
			thunkAPI.dispatch(setLoadingStatus(false))
			thunkAPI.dispatch(
				setErrorStatus({
					status: false,
					message: '',
				})
			)
			const getWinnerObj: ReqBodyWithHeaders = {
				method: APIMethods.GET,
				url: endPoints.getWinners.replace(
					'{platformName}',
					reqObj.platformName
				),
				sessionId: reqObj.sessionId,
				client: reqObj.client,
			}
			thunkAPI.dispatch(getWinnersList(getWinnerObj))
			return { result: response.result }
		} else {
			thunkAPI.dispatch(setLoadingStatus(false))
			thunkAPI.dispatch(
				setErrorStatus({
					status: true,
					message: 'Error! Please try after some time',
				})
			)
		}
	}
)

export const getWinnerTimeline = createAsyncThunk(
	'khc/winnersTimeline',
	async (reqObj: ReqBodyWithHeaders, thunkAPI) => {
		thunkAPI.dispatch(setLoadingStatus(true))
		const response = await fetchApi(reqObj, true)
		if (response) {
			thunkAPI.dispatch(setLoadingStatus(false))
			return response
		} else {
			thunkAPI.dispatch(setLoadingStatus(false))
		}
	}
)

export const userSlice = createSlice({
	name: 'user',
	initialState,
	reducers: {
		setLocation: (state, action: PayloadAction<UserLocationType>) => {
			state.latitude = action.payload.latitude
			state.longitude = action.payload.longitude
		},
		clearUserData: (state) => {
			state.email = ''
			state.name = ''
			state.gender = Gender.MALE
			state.sessionId = ''
			state.phone = ''
			state.hasPlacedOrder = false
			state.id = null
		},
		updateMobile: (state, action: PayloadAction<number | ''>) => {
			state.phone = action.payload
		},
		updateTimelineId: (state, action: PayloadAction<string>) => {
			state.timelineId = action.payload
		},
	},
	extraReducers: (builder) => {
		builder.addCase(checkVotingClose.fulfilled, (state, action) => {
			setCookie({
				name: 'frontend',
				value: action.payload?.sessionId,
				days: 30,
				mins: 0,
			})
			state.sessionId = action.payload?.sessionId
			state.startDateTime = action.payload?.startTime
			state.endDateTime = action.payload?.endTime
		})
		builder.addCase(getCurrentUserDetails.fulfilled, (state, action) => {
			setCookie({
				name: 'frontend',
				value: action.payload?.sessionId,
				days: 30,
				mins: 0,
			})
			state.sessionId = action.payload?.sessionId
			state.email = action.payload?.userObj.email
			state.gender = action.payload?.userObj.gender
			state.name = action.payload?.userObj.name
			state.phone = action.payload?.userObj.phone
			state.hasPlacedOrder = action.payload?.userObj.hasPlacedOrder
			state.id = action.payload?.userObj.id
		})
		builder.addCase(generateNewSession.fulfilled, (state, action) => {
			setCookie({
				name: 'frontend',
				value: action.payload?.sessionId,
				days: 30,
				mins: 0,
			})
			state.sessionId = action.payload?.sessionId
		})
		builder.addCase(getUserWinnings.fulfilled, (state, action) => {
			state.userWinning = action.payload?.result
		})
		builder.addCase(getWinnersList.fulfilled, (state, action) => {
			state.winnerList = action.payload?.result
		})
		builder.addCase(getWinnerTimeline.fulfilled, (state, action) => {
			state.startDateTime = action.payload.startDateTime
			state.endDateTime = action.payload.endDateTime
		})
	},
})

export const { updateMobile, clearUserData, setLocation, updateTimelineId } =
	userSlice.actions

export default userSlice.reducer
